import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";

const Row = ({ label, value, accent }) => (
  <div className="flex items-baseline justify-between gap-4 py-2 border-b border-dashed border-[var(--rs-border)] last:border-0">
    <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--rs-text-muted)]">{label}</span>
    <span className={`text-sm font-bold text-right ${accent ? "text-[var(--rs-primary)]" : "text-[var(--rs-text)]"}`}>
      {value}
    </span>
  </div>
);

const fmtDt = (dt) => {
  if (!dt) return "—";
  try {
    const d = new Date(dt);
    if (isNaN(d.getTime())) return dt;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}.${mm}.${yyyy} · ${hh}:${mi}`;
  } catch (e) {
    return dt;
  }
};

const RentalSummary = ({ vehicle, customer, rentalType, rentalStart, rentalEnd, durationHours, expectedKm, priceChf, station, days }) => {
  const { t } = useLanguage();
  const isMaster = rentalType === "master_package";
  return (
    <aside className="bg-[var(--rs-surface)] border-r-2 border-[var(--rs-text)] p-6 lg:p-8" data-testid="rental-summary">
      <div className="rs-overline">{t("summary_title")}</div>
      <h2 className="font-[Chivo] text-2xl font-black mt-1 leading-tight">NIVARA</h2>
      <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--rs-text-muted)] mt-1">{t("summary_brand_tag")}</div>

      <div className="mt-8">
        <div className="rs-overline mb-3">{t("summary_vehicle")}</div>
        {vehicle ? (
          <div className="bg-white border border-[var(--rs-border)] p-4">
            <div className="text-sm font-bold uppercase tracking-tight">{vehicle.name}</div>
            <div className="text-xs text-[var(--rs-text-muted)] mt-1">
              {t("plate")}: <span className="text-[var(--rs-text)] font-bold">{vehicle.plate}</span>
            </div>
            <div className="text-xs text-[var(--rs-text-muted)] mt-1">
              {vehicle.type} · {vehicle.capacity}
            </div>
          </div>
        ) : (
          <div className="text-sm text-[var(--rs-text-muted)] italic">{t("summary_no_vehicle")}</div>
        )}
      </div>

      <div className="mt-8">
        <div className="rs-overline mb-3">{t("summary_terms")}</div>
        {isMaster ? (
          <>
            <Row label={t("term_master_packages")} value="3h / 6h / 10h" />
            <Row label={t("term_extra_km")} value="+ CHF 0.35/km" />
            <Row label={t("term_overtime")} value="+ CHF 25/h" />
          </>
        ) : (
          <>
            <Row label={t("term_daily_ch")} value={t("term_from") + " CHF 190.–"} />
            <Row label={t("term_abroad_eu")} value={t("term_from") + " CHF 230.–"} />
            <Row label={t("term_km_limit")} value={t("term_km_none")} />
          </>
        )}
        <Row label={t("summary_deposit")} value="CHF 300.–" />
        <Row label={t("summary_deductible")} value="CHF 1'000.–" />
        <Row label={t("summary_fuel")} value={t("summary_fuel_value")} />
      </div>

      {station && (
        <div className="mt-6">
          <div className="rs-overline mb-2">{t("pickup_location")}</div>
          <div className="text-sm font-bold">{station.name}</div>
          <div className="text-xs text-[var(--rs-text-muted)]">{station.full}</div>
        </div>
      )}

      {(rentalStart || rentalEnd) && (
        <div className="mt-6">
          <div className="rs-overline mb-3">{t("th_period")}</div>
          <div className="text-xs">{fmtDt(rentalStart)}</div>
          <div className="text-xs text-[var(--rs-text-muted)]">↓</div>
          <div className="text-xs">{fmtDt(rentalEnd)}</div>
          {durationHours > 0 && (
            <div className="text-xs mt-2">
              <span className="rs-overline mr-2">{t("duration")}</span>
              {rentalType === "vito_daily" || rentalType === "daily" ? (
                <span className="font-bold">{days || Math.max(1, Math.ceil(durationHours / 24))} {(days || Math.ceil(durationHours / 24)) === 1 ? "Tag" : "Tage"}</span>
              ) : (
                <span className="font-bold">{Number(durationHours).toFixed(1)} {t("hours_short")}</span>
              )}
            </div>
          )}
          {isMaster && expectedKm > 0 && (
            <div className="text-xs">
              <span className="rs-overline mr-2">{t("expected_km")}</span>
              <span className="font-bold">{expectedKm} km</span>
            </div>
          )}
        </div>
      )}

      {priceChf > 0 && (
        <div className="mt-6 bg-[var(--rs-primary)] text-white p-4">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">{t("total_price")}</div>
          <div className="font-[Chivo] text-3xl font-black mt-1">CHF {priceChf.toLocaleString("de-CH")}.–</div>
        </div>
      )}

      {customer?.full_name && (
        <div className="mt-8">
          <div className="rs-overline mb-3">{t("th_customer")}</div>
          <div className="text-sm font-bold">{customer.full_name}</div>
          {customer.address && (
            <div className="text-xs text-[var(--rs-text-muted)] mt-1">
              {customer.address}, {customer.postal_code} {customer.city}
            </div>
          )}
          {customer.license_number && (
            <div className="text-xs text-[var(--rs-text-muted)] mt-1">
              {t("f_license")}: {customer.license_number}
            </div>
          )}
        </div>
      )}

      <div className="mt-10 h-px bg-[var(--rs-border)]" />
      <div className="mt-3 text-[10px] tracking-[0.25em] uppercase text-[var(--rs-text-muted)]">
        NIVARA Group · St. Gallen · Solothurn · Bern
      </div>
    </aside>
  );
};

export default RentalSummary;
